/**
 * Hook de conexão Google Ads (OAuth + modal de conta/ações).
 * Stash + promise compartilhada evitam perda do modal no remount do Strict Mode.
 */
import { useCallback, useEffect, useState } from 'react';
import { notify } from '../../../utils/toast';
import { usePermissions } from '../../../hooks/usePermissions';
import {
    GoogleAdsOAuthRedirectPending,
    clearGoogleAdsModalStash,
    clearGoogleAdsPendingCode,
    readGoogleAdsModal,
    readGoogleAdsOAuthCallback,
    stashGoogleAdsModal,
    startGoogleAdsOAuthRedirect,
} from '../../../services/modules/googleAdsOAuth';
import {
    disconnectGoogleAds,
    exchangeGoogleAdsCode,
    getGoogleAdsStatus,
    listGoogleAdsConversionActions,
    listGoogleAdsCustomers,
    saveGoogleAdsIntegration,
    type GoogleAdsConversionAction,
    type GoogleAdsCustomerOption,
    type GoogleAdsStatus,
} from '../../../services/modules/googleAdsRepository';

export type GoogleAdsModalData = {
    customers: GoogleAdsCustomerOption[];
};

/** Dedupa exchange entre montagens do Strict Mode (código OAuth é single-use). */
let exchangePromise: Promise<{ customers: GoogleAdsCustomerOption[] }> | null = null;

function isGoogleAdsConnectionError(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : String(err);
    return /token google expirado|reconecte a conta|integração google|falha na api google ads|sem permissão para esta operação|forbidden/i.test(msg);
}

function applyGoogleAdsExpiredState(
    setStatus: (s: GoogleAdsStatus) => void,
    setModalData: (v: GoogleAdsModalData | null) => void,
) {
    clearGoogleAdsModalStash();
    setModalData(null);
    setStatus({ connected: false, status: 'expired' });
}

export function useGoogleConnection(organizationId: string | undefined) {
    const { canManageIntegrations } = usePermissions();
    const [status, setStatus] = useState<GoogleAdsStatus>({ connected: false });
    const [connecting, setConnecting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalData, setModalData] = useState<GoogleAdsModalData | null>(() => readGoogleAdsModal());
    const [actions, setActions] = useState<GoogleAdsConversionAction[]>([]);
    const [loadingActions, setLoadingActions] = useState(false);

    const openModal = useCallback((customers: GoogleAdsCustomerOption[]) => {
        stashGoogleAdsModal(customers);
        setModalData({ customers });
    }, []);

    const loadStatus = useCallback(async () => {
        if (!organizationId || !canManageIntegrations) return;
        try {
            const s = await getGoogleAdsStatus(organizationId);
            setStatus(s);
            return s;
        } catch {
            setStatus({ connected: false });
            return { connected: false } as GoogleAdsStatus;
        }
    }, [organizationId, canManageIntegrations]);

    useEffect(() => {
        if (!canManageIntegrations) return;
        loadStatus();
    }, [loadStatus, canManageIntegrations]);

    // Callback OAuth → exchange (com stash) ou retoma pending no servidor
    useEffect(() => {
        if (!organizationId || !canManageIntegrations) return;

        let cancelled = false;

        (async () => {
            try {
                const cb = readGoogleAdsOAuthCallback();
                if (cb) {
                    setConnecting(true);
                    if (!exchangePromise) {
                        exchangePromise = exchangeGoogleAdsCode(cb.code, cb.organizationId)
                            .then((result) => {
                                clearGoogleAdsPendingCode();
                                stashGoogleAdsModal(result.customers || []);
                                return { customers: result.customers || [] };
                            })
                            .finally(() => {
                                exchangePromise = null;
                            });
                    }
                    const result = await exchangePromise;
                    if (cancelled) return;
                    openModal(result.customers);
                    notify.success('Conta Google autorizada — escolha a conta e as conversões.');
                    await loadStatus();
                    return;
                }

                const s = await getGoogleAdsStatus(organizationId);
                if (cancelled) return;
                setStatus(s);

                // Pending: sempre rebusca contas (evita stash antigo só com IDs)
                if (s.status === 'pending') {
                    clearGoogleAdsModalStash();
                    setConnecting(true);
                    try {
                        const customers = await listGoogleAdsCustomers(organizationId);
                        if (cancelled) return;
                        openModal(customers);
                    } catch (err) {
                        if (cancelled) return;
                        if (isGoogleAdsConnectionError(err)) {
                            applyGoogleAdsExpiredState(setStatus, setModalData);
                            await loadStatus();
                            notify.warning('Conexão Google Ads expirou. Clique em Reconectar para autorizar novamente.');
                            return;
                        }
                        throw err;
                    }
                    return;
                }

                const stashed = readGoogleAdsModal();
                if (stashed) setModalData(stashed);
            } catch (err) {
                if (err instanceof GoogleAdsOAuthRedirectPending) return;
                clearGoogleAdsPendingCode();
                if (!cancelled) {
                    if (isGoogleAdsConnectionError(err)) {
                        applyGoogleAdsExpiredState(setStatus, setModalData);
                        await loadStatus();
                        notify.warning('Conexão Google Ads expirou. Clique em Reconectar para autorizar novamente.');
                    } else {
                        notify.error(err instanceof Error ? err.message : 'Falha no OAuth Google');
                    }
                }
            } finally {
                if (!cancelled) setConnecting(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [organizationId, openModal, loadStatus, canManageIntegrations]);

    const resumePending = useCallback(async () => {
        if (!organizationId) return;
        setConnecting(true);
        try {
            clearGoogleAdsModalStash();
            const customers = await listGoogleAdsCustomers(organizationId);
            openModal(customers);
            notify.success('Escolha a conta e as conversões.');
        } catch (err) {
            if (isGoogleAdsConnectionError(err)) {
                applyGoogleAdsExpiredState(setStatus, setModalData);
                await loadStatus();
                notify.warning('Conexão Google Ads expirou. Clique em Reconectar para autorizar novamente.');
            } else {
                notify.error(err instanceof Error ? err.message : 'Erro ao listar contas Google');
            }
        } finally {
            setConnecting(false);
        }
    }, [organizationId, openModal, loadStatus]);

    const reconnect = useCallback(async () => {
        if (!organizationId) {
            notify.error('Organização não encontrada');
            return;
        }
        setConnecting(true);
        try {
            try {
                await disconnectGoogleAds(organizationId);
            } catch {
                /* limpa estado local mesmo se a row já foi removida */
            }
            clearGoogleAdsModalStash();
            clearGoogleAdsPendingCode();
            setModalData(null);
            setActions([]);
            setStatus({ connected: false });
            startGoogleAdsOAuthRedirect(organizationId);
        } catch (err) {
            if (err instanceof GoogleAdsOAuthRedirectPending) return;
            notify.error(err instanceof Error ? err.message : 'Erro ao reconectar Google Ads');
            setConnecting(false);
        }
    }, [organizationId]);

    const connect = useCallback(async () => {
        if (!organizationId) {
            notify.error('Organização não encontrada');
            return;
        }
        if (status.status === 'pending') {
            await resumePending();
            return;
        }
        if (status.status === 'expired') {
            await reconnect();
            return;
        }
        setConnecting(true);
        try {
            startGoogleAdsOAuthRedirect(organizationId);
        } catch (err) {
            if (err instanceof GoogleAdsOAuthRedirectPending) return;
            notify.error(err instanceof Error ? err.message : 'Erro ao conectar Google');
            setConnecting(false);
        }
    }, [organizationId, status.status, resumePending, reconnect]);

    const loadActionsForCustomer = useCallback(async (customer: GoogleAdsCustomerOption) => {
        if (!organizationId) return;
        setLoadingActions(true);
        try {
            const list = await listGoogleAdsConversionActions(
                organizationId,
                customer.customerId,
                customer.loginCustomerId
            );
            setActions(list);
            if (list.length === 0) {
                notify.info('Nenhuma ação de conversão nesta conta. Selecione uma conta de anúncios, não o gerenciador (MCC).');
            }
        } catch (err) {
            notify.error(err instanceof Error ? err.message : 'Erro ao listar conversões');
            setActions([]);
        } finally {
            setLoadingActions(false);
        }
    }, [organizationId]);

    const save = useCallback(async (input: {
        customerId: string;
        customerName: string;
        conversionActionIds: string[];
        loginCustomerId?: string;
    }) => {
        if (!organizationId) return;
        setSaving(true);
        try {
            await saveGoogleAdsIntegration(organizationId, input);
            clearGoogleAdsModalStash();
            setModalData(null);
            setActions([]);
            await loadStatus();
            notify.success('Google Ads conectado.');
        } catch (err) {
            notify.error(err instanceof Error ? err.message : 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    }, [organizationId, loadStatus]);

    const disconnect = useCallback(async () => {
        if (!organizationId) return;
        try {
            await disconnectGoogleAds(organizationId);
            clearGoogleAdsModalStash();
            clearGoogleAdsPendingCode();
            setModalData(null);
            setStatus({ connected: false });
            notify.success('Google Ads desconectado.');
        } catch (err) {
            notify.error(err instanceof Error ? err.message : 'Erro ao desconectar');
        }
    }, [organizationId]);

    const closeModal = useCallback(() => {
        clearGoogleAdsModalStash();
        setModalData(null);
        setActions([]);
    }, []);

    return {
        status,
        connecting,
        saving,
        modalData,
        actions,
        loadingActions,
        connect,
        reconnect,
        disconnect,
        save,
        closeModal,
        loadActionsForCustomer,
        isModalOpen: modalData !== null,
    };
}
